import { useState, useCallback, useMemo, useEffect, ComponentType, SVGProps } from 'react'
import Highcharts from 'highcharts'

interface LegendItem {
    label: string
    color: string
    icon: ComponentType<SVGProps<SVGSVGElement>>
}

interface ChartVisibilityState {
    [key: string]: boolean
}

const getInitialVisibilityState = (legendItems: LegendItem[]): ChartVisibilityState => {
    return legendItems.reduce((acc, item) => {
        acc[item.label] = false
        return acc
    }, {} as ChartVisibilityState)
}

export const useChartLegend = (legendItems: LegendItem[], chart?: Highcharts.Chart) => {
    const [visibilityState, setVisibilityState] = useState<ChartVisibilityState>({})

    useEffect(() => {
        if (legendItems.length > 0) {
            setVisibilityState(getInitialVisibilityState(legendItems))
        }
    }, [legendItems])

    const hiddenItems = useMemo(() => {
        if (Object.keys(visibilityState).length === 0 && legendItems.length > 0) {
            return getInitialVisibilityState(legendItems)
        }
        return visibilityState
    }, [legendItems, visibilityState])

    const getVisibilityInfo = useCallback(
        (label: string) => {
            const visibleCount = legendItems.length - Object.values(visibilityState).filter(Boolean).length
            const isLastVisible = visibleCount === 1 && !visibilityState[label]
            return { visibleCount, isLastVisible }
        },
        [legendItems, visibilityState]
    )

    const showAllItems = useCallback(() => {
        setVisibilityState(getInitialVisibilityState(legendItems))
        chart?.series.forEach((s) => s.setVisible(true, false))
        chart?.redraw()
    }, [legendItems, chart])

    const toggleItem = useCallback(
        (index: number) => {
            const label = legendItems[index]?.label
            if (!label) return

            const { isLastVisible } = getVisibilityInfo(label)
            if (isLastVisible) {
                showAllItems()
                return
            }

            chart?.series[index]?.setVisible(visibilityState[label], true)

            setVisibilityState((prev) => ({
                ...prev,
                [label]: !prev[label],
            }))
        },
        [legendItems, chart, getVisibilityInfo, showAllItems, visibilityState]
    )

    const focusItem = useCallback(
        (index: number) => {
            const label = legendItems[index]?.label
            if (!label) return

            const { isLastVisible } = getVisibilityInfo(label)
            if (isLastVisible) {
                showAllItems()
                return
            }

            const focusState = legendItems.reduce((acc, item, idx) => {
                acc[item.label] = idx !== index
                return acc
            }, {} as ChartVisibilityState)

            chart?.series.forEach((s, idx) => s.setVisible(idx === index, false))
            chart?.redraw()
            setVisibilityState(focusState)
        },
        [legendItems, chart, getVisibilityInfo, showAllItems]
    )

    return {
        hiddenItems,
        toggleItem,
        focusItem,
    }
}
